// src/components/AuthForm.jsx
import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import { isAccessExpired } from '../context/accountUtils';

const AuthForm = ({ isLogin }) => {
  const { login } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [name, setName] = useState('');
  const [pin, setPin] = useState('');
  const [authTosCheckbox, setAuthTosCheckbox] = useState(false);
  const [userType, setUserType] = useState('individual');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // Prevent duplicate auth requests while waiting for the API.
  const [isAuthSubmitting, setIsAuthSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isForgotSubmitting, setIsForgotSubmitting] = useState(false);
  const [isResetSubmitting, setIsResetSubmitting] = useState(false);

  // For verification code UI
  const [code, setCode] = useState(Array(6).fill(''));
  const codeInputs = useRef([]);

  // For reset password UI
  const [newPw1, setNewPw1] = useState('');
  const [newPw2, setNewPw2] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  // ----- Determine mode from route (keeps compatibility with isLogin prop) -----
  const path = location.pathname || '';
  const mode =
    path.endsWith('/signup-code') ? 'verify' :
    path.endsWith('/forgot-password') ? 'forgot' :
    path.endsWith('/reset-password') ? 'reset' :
    (isLogin ? 'login' : 'signup');

  // ----- Existing banners handling (multiple sessions/expired) for login path -----
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('multipleusers')) {
      setError("You've been logged out because your account was used on another device.");
    } else if (params.get('expired')) {
      setError('Session expired. Please log in again.');
    } else if (params.get('verified')) {
      setInfo('Email verified! You can log in now.');
    }
  }, [location]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const expired = urlParams.get('expired');
    const multiple = urlParams.get('multipleusers');
    const token = localStorage.getItem('token');
    if (mode === 'login' && token && (expired === '1' || multiple === '1')) {
      fetch('https://jmew.xyz/aac/api/clear_token.php', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      }).catch(() => {});
    }
  }, [mode]);

  // ----- If we landed on /signup-code with ?email=..., prefill email and focus first code box -----
  useEffect(() => {
    if (mode === 'verify') {
      const params = new URLSearchParams(location.search);
      const e = params.get('email') || '';
      if (e) setEmail(e);
      setTimeout(() => codeInputs.current?.[0]?.focus(), 0);
    }
    if (mode === 'reset') {
      // Could show the email in subtitle via URL param if present
      const params = new URLSearchParams(location.search);
      const e = params.get('email') || '';
      if (e) setEmail(e);
    }
  }, [mode, location.search]);

  // ----- Shared submit handler for login & signup -----
  const onSubmitAuth = async (e) => {
    e.preventDefault();
    if (isAuthSubmitting) return;

    setError('');
    setInfo('');
    setIsAuthSubmitting(true);

    try {
      const endpoint = mode === 'login' ? '/login.php' : '/signup.php';
      const payload = mode === 'login'
        ? { email, password }
        : { email, password, name, pin, user_type: userType, tos_agreed: authTosCheckbox };

      const res = await api.post(endpoint, payload);
      const json = typeof res.data === 'string'
        ? JSON.parse(res.data.match(/{.*}/s)?.[0] || '{}')
        : res.data;

      if (json.error) throw new Error(json.error);

      // If signup flow is verification-first, redirect to verify screen
      if (mode === 'signup' && json?.next === 'signup-code') {
        navigate(`/signup-code?email=${encodeURIComponent(email)}`);
        return;
      }

      // Standard login success
      const { token, user_id, name: resName, pin: resPin, smart_setup_status } = json;
      if (!token || !user_id) throw new Error('Login failed. No token or user ID returned.');

      login(token, user_id, resName, resPin);
      if (isAccessExpired()) {
        console.log('Redirecting to profile due to access expiry after login');
        navigate('/profile?upgrade=1');
        return;
      }
      if (smart_setup_status === 'pending') {
        navigate('/smart-setup', { replace: true });
        return;
      }

      navigate('/boards', { replace: true });
    } catch (err) {
      if (err?.response?.data) {
        const serverError = typeof err.response.data === 'string'
          ? JSON.parse(err.response.data.match(/{.*}/s)?.[0] || '{}')
          : err.response.data;

        // If API says to verify first
        if (serverError?.next === 'signup-code' && serverError?.email) {
          navigate(`/signup-code?email=${encodeURIComponent(serverError.email)}`);
          return;
        }
        setError(serverError.error || 'Invalid input');
      } else {
        setError(err.message || 'Something went wrong');
      }
    } finally {
      setIsAuthSubmitting(false);
    }
  };

  // ----- Handlers for verification code UI -----
  const handleCodeChange = (i, v) => {
    const val = v.replace(/\D/g, '').slice(0, 1);
    const next = [...code]; next[i] = val; setCode(next);
    if (val && i < 5) codeInputs.current[i + 1]?.focus();
  };
  const handleCodeKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !code[i] && i > 0) codeInputs.current[i - 1]?.focus();
  };
  const handleCodePaste = (e) => {
    const txt = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (txt.length) {
      const next = Array(6).fill('');
      for (let i = 0; i < txt.length; i++) next[i] = txt[i];
      setCode(next);
      codeInputs.current[Math.min(5, txt.length)]?.focus();
      e.preventDefault();
    }
  };
  const submitVerification = async () => {
    if (isVerifying) return;

    setError('');
    setInfo('');
    const joined = code.join('');
    if (joined.length !== 6) { setError('Enter the 6-digit code.'); return; }

    setIsVerifying(true);
    try {
      const { data } = await api.post('/verify_email.php', { email, code: joined });
      if (data?.token && data?.user_id) {
        // login(data.token, data.user_id, data.name, data.pin);
        navigate('/login?verified=1');
      } else {
        setError('Verification failed');
      }
    } catch (err) {
      const serverError = err?.response?.data;
      setError((typeof serverError === 'object' && serverError?.error) ? serverError.error : 'Verification failed');
    }
  };
  const resendVerification = async () => {
    if (isResending) return;

    setError('');
    setInfo('');
    setIsResending(true);
    try {
      await api.post('/resend_verify_code.php', { email });
      setInfo('A new code was sent to your email.');
    } catch (err) {
      const serverError = err?.response?.data;
      setError((typeof serverError === 'object' && serverError?.error) ? serverError.error : 'Could not resend code');
    } finally {
      setIsResending(false);
    }
  };

  // ----- Forgot password -----
  const submitForgot = async (e) => {
    e.preventDefault();
    if (isForgotSubmitting) return;

    setError('');
    setInfo('');
    setIsForgotSubmitting(true);
    try {
      await api.post('/forgot_password.php', { email });
      setInfo('If the email exists, a reset link has been sent.');
    } catch (_e) {
      setError('Something went wrong.');
    } finally {
      setIsForgotSubmitting(false);
    }
  };

  // ----- Reset password -----
  const submitReset = async (e) => {
    e.preventDefault();
    if (isResetSubmitting) return;

    setError('');
    setInfo('');
    if (newPw1 !== newPw2) { setError('Passwords do not match'); return; }

    setIsResetSubmitting(true);
    try {
      const params = new URLSearchParams(location.search);
      const token = params.get('token') || '';
      const resetEmail = email || params.get('email') || '';
      await api.post('/reset_password.php', {
        email: resetEmail,
        token,
        new_password: newPw1,
        repeat_password: newPw2
      });
      setInfo('Password updated. You can log in now.');
      setTimeout(() => navigate('/login'), 1000);
    } catch (err) {
      const serverError = err?.response?.data;
      setError((typeof serverError === 'object' && serverError?.error) ? serverError.error : 'Reset failed');
    } finally {
      setIsResetSubmitting(false);
    }
  };

  // ===========================
  //          RENDER
  // ===========================
  return (
    <div className="auth-landing">
      {/* Left: Brand / Hero */}
      <aside className="auth-hero">
        <img
          src="/aac/api/imgs/feature-graphic.png"
          alt="iBoard — AAC Board"
          className="auth-hero__logo"
        />
        <h2 className="auth-hero__title">We make communication simpler.</h2>
        <p className="auth-hero__text">
          iBoard helps you build colorful AAC boards, speak with TTS, and work both online and offline.
        </p>
        <ul className="auth-hero__bullets">
          <li>Fast, touch-friendly boards</li>
          <li>Works offline with sync</li>
          <li>ARASAAC & custom images</li>
        </ul>
      </aside>

      {/* Right: Card with the form */}
      <main className="auth-pane">
        <div className="auth-card">
          {/* Header */}
          <div className="auth-card__header">
            {mode === 'login' && (
              <>
                <h1 className="auth-card__title">Sign in</h1>
                <p className="auth-card__subtitle">Welcome back to iBoard</p>
              </>
            )}
            {mode === 'signup' && (
              <>
                <h1 className="auth-card__title">Create account</h1>
                <p className="auth-card__subtitle">Start in under a minute</p>
              </>
            )}
            {mode === 'verify' && (
              <>
                <h1 className="auth-card__title">Verify your email address</h1>
                <p className="auth-card__subtitle">
                  We've sent you a verification code. You need to verify before logging in.
                </p>
              </>
            )}
            {mode === 'forgot' && (
              <>
                <h1 className="auth-card__title">Forgot password</h1>
                <p className="auth-card__subtitle">Enter your email and we’ll send a reset link.</p>
              </>
            )}
            {mode === 'reset' && (
              <>
                <h1 className="auth-card__title">Reset password</h1>
                <p className="auth-card__subtitle">{email}</p>
              </>
            )}
          </div>

          {/* Alerts */}
          {error && <div className="auth-alert">{error}</div>}
          {info && !error && <div className="auth-alert-info">{info}</div>}

          {/* LOGIN / SIGNUP FORMS */}
          {(mode === 'login' || mode === 'signup') && (
            <form className="auth-form" onSubmit={onSubmitAuth}>
              {mode === 'signup' && (
                <>
                  <div className="auth-field">
                    <label>Name</label>
                    <input type="text" value={name} onChange={(e)=>setName(e.target.value)} placeholder="Your name" required />
                  </div>

                  <div className="auth-field">
                    <label>4-digit PIN</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      value={pin}
                      onChange={(e)=>setPin(e.target.value.replace(/\D/g,''))}
                      placeholder="1234"
                      required
                    />
                  </div>

                  <div className="auth-field">
                    <label>Account type</label>
                    <select value={userType} onChange={(e)=>setUserType(e.target.value)} disabled required style={{ width: '100%' }}>
                      <option value="individual">Individual</option>
                      {/* <option value="organisation" disabled>Organisation</option> */}
                    </select>
                  </div>
                </>
              )}

              <div className="auth-field">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e)=>setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div className="auth-field">
                <label>Password</label>
                <div className="auth-password">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e)=>setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    className="auth-password__toggle"
                    onClick={()=>setShowPw(v=>!v)}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    {showPw ? 'Hide' : 'Show'}
                  </button>
                </div>

                {mode === 'signup' && (
                  <div style={{ marginTop: '22px', textAlign: 'center' }} className="auth-tos">
                    <label>
                      <input
                        id="auth-tos-checkbox"
                        onChange={(e) => setAuthTosCheckbox(e.target.checked)}
                        style={{ height: '20px', width: '15px', verticalAlign: 'middle', marginRight: '8px' }}
                        type="checkbox"
                        required
                      />
                      I agree to the <a href="https://jmew.xyz/tos" className="auth-link">Terms of Service</a> and <a href="https://jmew.xyz/privacy" className="auth-link">Privacy Policy</a>
                    </label>
                  </div>
                )}
              </div>

              <button type="submit" className="auth-submit" disabled={isAuthSubmitting}>
                {isAuthSubmitting
                  ? (mode === 'login' ? 'Logging in...' : 'Creating account...')
                  : (mode === 'login' ? 'Log in now' : 'Create account')}
              </button>

              {/* Forgot password link for login */}
              {mode === 'login' && (
                <p className="auth-switch" style={{ marginTop: 8 }}>
                  <a href="/aac/forgot-password" className="auth-link">Forgot your password?</a>
                </p>
              )}
            </form>
          )}

          {/* SIGNUP CODE (VERIFY) */}
          {mode === 'verify' && (
            <div className="auth-form">
              <p>Your verification code has been sent to your email, please check your junk/spam folder.</p>
              <div className="auth-field">
                <label>Email</label>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{email}</span>
                  <a href="/aac/signup" className="auth-link">Change email</a>
                </div>
              </div>

              <div className="auth-field">
                <label>Verification code</label>
                <div style={{ display: 'flex', gap: 8 }} onPaste={handleCodePaste}>
                  {code.map((c, i) => (
                    <input
                      key={i}
                      ref={el => codeInputs.current[i] = el}
                      inputMode="numeric"
                      value={c}
                      onChange={(e)=>handleCodeChange(i, e.target.value)}
                      onKeyDown={(e)=>handleCodeKeyDown(i, e)}
                      maxLength={1}
                      style={{ width: 44, height: 48, textAlign: 'center', fontSize: 20, borderRadius: 10, border: '1px solid #e5e7eb', background: '#f8fafc' }}
                    />
                  ))}
                </div>
              </div>

              <button type="button" className="auth-submit" onClick={submitVerification} disabled={isVerifying}>
                {isVerifying ? 'Verifying...' : 'Verify & Continue'}
              </button>

              <div className="auth-switch" style={{ marginTop: 12 }}>
                Not received the code?{' '}
                <button
                  type="button"
                  onClick={resendVerification}
                  className="auth-link"
                  disabled={isResending}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: isResending ? 'default' : 'pointer' }}
                >
                  {isResending ? 'Sending...' : 'Resend'}
                </button>
              </div>
              <p className="auth-switch">
                Changed your mind? <a href="/aac/login" className="auth-link">Log in</a>
            </p>
            </div>
          )}

          {/* FORGOT PASSWORD */}
          {mode === 'forgot' && (
            <form className="auth-form" onSubmit={submitForgot}>
              <div className="auth-field">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e)=>setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <button type="submit" className="auth-submit" disabled={isForgotSubmitting}>
                {isForgotSubmitting ? 'Sending...' : 'Send reset link'}
              </button>
              <p className="auth-switch">
                Changed your mind? <a href="/aac/login" className="auth-link">Log in</a>
            </p>
            </form>
          )}

          {/* RESET PASSWORD */}
          {mode === 'reset' && (
            <form className="auth-form" onSubmit={submitReset}>
              <div className="auth-field">
                <label>New password</label>
                <input
                  type="password"
                  value={newPw1}
                  onChange={(e)=>setNewPw1(e.target.value)}
                  required
                />
              </div>
              <div className="auth-field">
                <label>Repeat new password</label>
                <input
                  type="password"
                  value={newPw2}
                  onChange={(e)=>setNewPw2(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="auth-submit" disabled={isResetSubmitting}>
                {isResetSubmitting ? 'Updating...' : 'Update password'}
              </button>
            <p className="auth-switch">
                Changed your mind? <a href="/aac/login" className="auth-link">Log in</a>
            </p>
            </form>
          )}
          {/* Bottom switch between login/signup */}
          {(mode === 'login' || mode === 'signup') && (
            <p className="auth-switch">
              {mode === 'login' ? (
                <>Don’t have an account? <a href="/aac/signup" className="auth-link">Sign up</a></>
              ) : (
                <>Already have an account? <a href="/aac/login" className="auth-link">Log in</a></>
              )}
            </p>
          )}
        </div>
      </main>
    </div>
  );
};

export default AuthForm;
